// [ai] The route table: every URL the old server answered is a route of the shell (docs/superpowers/specs/2026-09-13-app-shell-refactor.md,
// "Server port"). `handle.label` is the crumb when the tree has no entry for the location
// and the page names nothing (a document names itself through the shell store).
import { matchRoutes, Navigate, type RouteObject } from "react-router";
import { AppShell } from "./AppShell";
import { Pending } from "../pages/Pending";
import { NotFound } from "../pages/NotFound";
import { BrowsePage } from "../pages/Browse";
import { PlayPage } from "../pages/Play";
import { SettingsPage } from "../pages/Settings";
import { HomePage } from "../pages/Home";
import { DocPage, isDocPath, isSectionRoot } from "../pages/Doc";
import { JournalPage } from "../pages/Journal";
import { RunnerPage } from "../pages/Runner";
import { InboxPage } from "../pages/Inbox";
import { IngestPage } from "../pages/Ingest";
import { ImportBatchPage, ImportDocPage, ImportsPage } from "../pages/Imports";
import { ReviewPage } from "../pages/Review";
import { ChatPage } from "../pages/Chat";
import { ViewPage } from "../pages/View";
import { ViewsPage } from "../pages/Views";
import { WorkbookPage } from "../pages/questionnaire/Workbook";
import { RegisterPage } from "../pages/questionnaire/Register";
import { TimelinePage } from "../pages/life/Timeline";
import { LifePage } from "../pages/life/Life";

export type RouteHandle = { label: string; kind: "page" | "doc" | "404" };
const page = (path: string, label: string, element: React.ReactElement, kind: RouteHandle["kind"] = "page"): RouteObject => ({ path, element, handle: { label, kind } satisfies RouteHandle });
const doc = (path: string, label: string) => page(path, label, <DocPage />, "doc");

export const ROUTES: RouteObject[] = [{
  path: "/", element: <AppShell />, handle: { label: "", kind: "page" } satisfies RouteHandle,
  children: [
    { index: true, element: <HomePage />, handle: { label: "", kind: "page" } satisfies RouteHandle },
    { path: "index.html", element: <Navigate to="/" replace />, handle: { label: "", kind: "page" } satisfies RouteHandle },
    page("play/:id", "Listen", <PlayPage />), page("browse", "Browse", <BrowsePage />), page("review", "Citation review", <ReviewPage />), page("settings", "Settings", <SettingsPage />),
    page("journal", "Journal", <JournalPage />), doc("journal/:year/:file", "Journal"), page("do/:name/:date?", "Today", <RunnerPage />),
    page("ingest", "Ingest", <IngestPage />), page("inbox", "Inbox", <InboxPage />), page("imports", "Imports", <ImportsPage />), page("imports/:id", "Import", <ImportBatchPage />), page("imports/:id/doc/*", "Import", <ImportDocPage />),
    page("chat", "Astra AI", <ChatPage />), page("views", "Views", <ViewsPage />), page("views/:slug", "View", <ViewPage />),
    page("life", "Life", <LifePage />), page("timeline", "Timeline", <TimelinePage />),
    page("workbook", "Workbook", <WorkbookPage />), page("register", "Question register", <RegisterPage />),
    { path: "QUESTIONNAIRE.html", element: <Navigate to="/workbook" replace />, handle: { label: "", kind: "page" } satisfies RouteHandle },
    doc("digests/*", "Digests"), doc("profile/*", "Profile"), doc("pages/*", "Pages"), doc("dossier/*", "Dossier"),
    doc(":doc", ""),   // /D1.html and every registered or discovered document; anything else is NotFound
    { path: "*", element: <NotFound />, handle: { label: "Not found", kind: "404" } satisfies RouteHandle },
  ],
}];
export { isDocPath };

/** The crumb a route gives on its own (the tree's title wins when it has the location; a document names itself). */
export function routeLabel(pathname: string): string {
  const leaf = matchRoutes(ROUTES, pathname)?.at(-1)?.route; const h = leaf?.handle as RouteHandle | undefined;
  if (!h) return "Not found";
  if (h.kind === "doc") return isDocPath(pathname) || isSectionRoot(pathname) ? "" : "Not found";
  return h.label;
}

/** The matched route pattern (for the seam: `/play/:id`, not the id). */
export function routePattern(pathname: string): string {
  const m = matchRoutes(ROUTES, pathname); if (!m) return "*";
  return "/" + m.slice(1).map(x => x.route.path ?? "").filter(Boolean).join("/");
}
